import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Fingerprint, ArrowLeft, Trash2, Plus, ShieldCheck, KeyRound } from 'lucide-react';

import { isSupported as webauthnSupported, enrolPasskey } from '@/lib/webauthn-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

const RECOVERY_LABELS = {
  email_link: 'Email magic link (single factor)',
  email_sms:  'Email link + SMS OTP (two factor — recommended)',
  admin:      'Admin-mediated reset (contact support)',
};

const PasskeySettings = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mojo_user') || 'null');
  const isLoggedIn = !!user?.id;

  const [credentials, setCredentials] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [recoveryMethod, setRecoveryMethod] = useState('');

  const supported = useMemo(() => webauthnSupported(), []);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const [credsR, statusR] = await Promise.all([
        axios.get(`${API}/webauthn/credentials`),
        axios.get(`${API}/webauthn/status`),
      ]);
      setCredentials(credsR.data?.credentials || []);
      setStatus(statusR.data || null);
      setRecoveryMethod(statusR.data?.recovery_method || '');
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate('/');
        return;
      }
      toast.error('Failed to load passkey settings');
    }
  };

  const enrol = async () => {
    if (!supported) {
      toast.error('Your browser does not support passkeys');
      return;
    }
    setBusy(true);
    try {
      const beginR = await axios.post(`${API}/webauthn/register/begin`);
      const publicKey = JSON.parse(beginR.data.publicKey);
      const credential = await enrolPasskey(publicKey);
      await axios.post(`${API}/webauthn/register/finish`, {
        credential,
        friendly_name: (newName || '').trim() || null,
      });
      toast.success('Passkey enrolled');
      setNewName('');
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Enrolment failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this passkey? You will need to enrol it again to use it.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/webauthn/credentials/${id}`);
      toast.success('Passkey revoked');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  const saveRecovery = async (method) => {
    if (!method) return;
    setBusy(true);
    try {
      await axios.put(`${API}/webauthn/recovery-method`, { method });
      setRecoveryMethod(method);
      toast.success('Recovery method updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update recovery method');
    } finally {
      setBusy(false);
    }
  };

  const active = credentials.filter((c) => c.is_active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F0FF] to-white p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/customer/dashboard')} className="mb-4" data-testid="passkeys-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to dashboard
        </Button>

        <Card className="mb-6 bg-gradient-to-br from-[#1B0C34] to-[#2D1854] text-white">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Fingerprint className="w-6 h-6" /> Passkeys
            </CardTitle>
            <CardDescription className="text-white/70">
              Replace your SMS OTP with your face, fingerprint, or hardware security key. Faster login, immune to SIM-swap attacks.
            </CardDescription>
          </CardHeader>
        </Card>

        {!supported && (
          <Card className="mb-4 border-amber-400">
            <CardContent className="pt-4 text-sm text-amber-700">
              Your browser does not support passkeys. Upgrade to a recent Chrome / Safari / Edge / Firefox to enable them.
            </CardContent>
          </Card>
        )}

        {/* Recovery method picker */}
        <Card className="mb-4" data-testid="passkey-recovery-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="w-4 h-4" /> Lost-device recovery
            </CardTitle>
            <CardDescription>
              How should we let you back in if you lose your device? Pick before enrolling your first passkey.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={recoveryMethod} onValueChange={(v) => saveRecovery(v)}>
              <SelectTrigger data-testid="passkey-recovery-select">
                <SelectValue placeholder="Pick a recovery method…" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(RECOVERY_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {recoveryMethod && (
              <p className="text-xs text-slate-500 mt-2">Current: {RECOVERY_LABELS[recoveryMethod]}</p>
            )}
          </CardContent>
        </Card>

        {/* Enrol */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add a passkey
            </CardTitle>
            <CardDescription>
              You can register more than one device (e.g. phone + laptop). Hardware security keys (YubiKey, Titan) also work.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="pk-name">Device name (optional)</Label>
              <Input
                id="pk-name"
                placeholder='e.g. "iPhone 15", "Work laptop"'
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={120}
                data-testid="passkey-name-input"
              />
            </div>
            <Button
              onClick={enrol}
              disabled={busy || !supported || !recoveryMethod}
              className="bg-[#F2B10C] text-[#1B0C34] hover:bg-[#d99a0a]"
              data-testid="passkey-enrol-btn"
            >
              <Fingerprint className="w-4 h-4 mr-1" />
              {busy ? 'Working…' : 'Enrol passkey'}
            </Button>
          </CardContent>
          {!recoveryMethod && (
            <CardContent className="pt-0 -mt-3">
              <p className="text-xs text-rose-600">Pick a recovery method above before enrolling.</p>
            </CardContent>
          )}
        </Card>

        {/* List */}
        <Card data-testid="passkey-list-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Your passkeys ({active.length} active)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {credentials.length === 0 ? (
              <p className="text-sm text-slate-500">No passkeys yet — enrol one above to start.</p>
            ) : (
              <ul className="space-y-2">
                {credentials.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center justify-between p-3 rounded-md border"
                    data-testid={`passkey-row-${c.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-semibold">
                        {c.friendly_name || 'Passkey'}
                        {!c.is_active && <span className="ml-2 text-xs text-rose-600">(revoked)</span>}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {c.transports || '—'} · enrolled {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                        {c.last_used_at && ` · last used ${new Date(c.last_used_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    {c.is_active && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revoke(c.id)}
                        disabled={busy}
                        data-testid={`passkey-revoke-btn-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Revoke
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-slate-500 mt-6 text-center">
          Passkeys are stored on your device(s) only. Mojo Money never sees your fingerprint or face data.
        </p>
      </div>
    </div>
  );
};

export default PasskeySettings;
